import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { useTranslation } from "../../../localization/useTranslation";
import { ActivityFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";

export default function useActivityDisplayFields() {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();

    function getTableFields(): Array<TableFields> {
        const fields: Array<TableFields> = [
            {
                id: "activity",
                name: translateString("activity"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
                mandatory: true,
            },
            {
                id: "id",
                name: translateString("id"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "type",
                name: translateString("type"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "status",
                name: translateString("status"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "evaluation",
                name: translateString("evaluation"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "priority",
                name: translateString("priority"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "tags",
                name: translateString("tags"),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },

            {
                id: "description",
                name: translateString("description"),
                grouping: ActivityFieldGrouping.DescriptionAndScope,
            },
            {
                id: "sub-tasks",
                name: translateString("subTasks"),
                grouping: ActivityFieldGrouping.DescriptionAndScope,
            },
            {
                id: "documentation-required",
                name: translateString("documentationRequired"),
                grouping: ActivityFieldGrouping.DescriptionAndScope,
            },

            {
                id: "start-date",
                name: translateString("startDate"),
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "end-date",
                name: translateString("endDate"),
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "recurrence",
                name: translateString("recurrence"),
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "days-prior-to-notification",
                name: translateString("daysPriorToNotification"),
                grouping: ActivityFieldGrouping.Scheduling,
            },

            {
                id: "responsible",
                name: translateString("responsible"),
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },
            {
                id: "assignees",
                name: translateString("assignees"),
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },
            {
                id: "approved-before-completed",
                name: translateString("approvedBeforeCompleted"),
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },

            {
                id: "business-areas",
                name: translateString("businessAreas"),
                grouping: ActivityFieldGrouping.Organization,
            },
            {
                id: "group-companies",
                name: translateString("groupCompanies"),
                grouping: ActivityFieldGrouping.Organization,
            },
            {
                id: "compliance-areas",
                name: translateString("complianceAreas"),
                grouping: ActivityFieldGrouping.Organization,
            },
            {
                id: "frameworks",
                name: translateString("frameworks"),
                grouping: ActivityFieldGrouping.Organization,
            },

            {
                id: "contracts",
                name: translateString("contracts"),
                grouping: ActivityFieldGrouping.ContractsAndRelations,
            },
            {
                id: "external-contract-parties",
                name: translateString("externalContractParties"),
                grouping: ActivityFieldGrouping.ContractsAndRelations,
            },
            {
                id: "internal-contract-parties",
                name: translateString("internalContractParties"),
                grouping: ActivityFieldGrouping.ContractsAndRelations,
            },

            {
                id: "status-timeline",
                name: translateString("statusTimeline"),
                grouping: ActivityFieldGrouping.StatusTimeline,
                tooltip: translateString("statusTimelineInfo"),
            },
        ];

        let result = fields;

        if (!permissions.contractManagementPermissions.canAccessContractsOverview) {
            result = result.filter((f) => f.grouping !== ActivityFieldGrouping.ContractsAndRelations);
        }

        if (!platformFeatures.annualWheelComplianceArea) {
            result = result.filter((f) => f.id !== "compliance-areas");
        }

        if (!permissions.frameworkPermissions.read) {
            result = result.filter((f) => f.id !== "frameworks");
        }

        return result;
    }

    const tableFields = getTableFields();

    return { tableFields };
}
